import extend from "extend";
import onSrcLoad from "1dr-utils/helpers/onSrcLoad";
import elsForEach from "1dr-utils/helpers/elsForEach";
import Velocity from "velocity-animate";

window.Velocity = Velocity;

const Deferred = function () {
  this.resolve = null;
  this.reject = null;

  this.promise = new Promise(
    function (r, rj) {
      this.resolve = r;
      this.reject = rj;
    }.bind(this),
  );

  this.then = this.promise.then;
};

window.Deferred = Deferred;

// Random number between a and b
const rand = function (a, b) {
  let range = b - a;
  let n = Math.round(Math.random() * range);
  return a + n;
};

// Save time on some css styles:
const Css = {
  opacity: function (el, o) {
    el.style.opacity = o;
  },

  z: function (el, z) {
    el.style.zIndex = z;
  },

  backgroundImage: function (el, src) {
    if (!src) {
      el.style.backgroundImage = "";
    } else {
      el.style.backgroundImage = "url(" + src + ")";
    }
  },
};

/*========================================
=  Settings, config, defaults            =
========================================*/

let directions = ["top-left", "bottom-right", "top-right", "bottom-left"];

let defaults = {
  distance: 0,
  duration: 8000,
  minPan: 0,
  z: 1.05,
  playNextAt: 4000,
  fadeInDelay: 1000,
  fadeOutDelay: 1000,
  fadeDuration: 1200,
};

/*====================================
=            Slide Object            =
====================================*/

const Slide = function (el, parent) {
  let self = this;
  self.el = el;
  self.slideshow = parent;

  self.loaded = false;
  self.rejected = false;
  self.promise = new Deferred();

  return self;
};

Slide.prototype.fadeOut = function (delay) {
  let el = this.el;
  let self = this;

  Velocity(
    el,
    { opacity: 0 },
    {
      queue: false,
      begin: function () {
        Css.z(el, 0);
      },
      duration: self.slideshow.options.fadeDuration,
      delay: delay,
    },
  );
};

Slide.prototype.fadeIn = function (delay) {
  let el = this.el;
  let self = this;

  Velocity(
    el,
    { opacity: 1 },
    {
      queue: false,
      begin: function () {
        Css.z(el, 1);
      },
      duration: self.slideshow.options.fadeDuration,
      delay: delay,
    },
  );
};

Slide.prototype.panIfLoaded = function (isStart) {
  let self = this;

  if (self.loaded) {
    self.pan(isStart);
    return;
  } else {
    this.load();
  }

  // wait for load to resolve first:
  self.promise.promise.then(function () {
    self.pan(isStart);
  });
};

Slide.prototype.load = function () {
  let self = this;

  if (self.loaded) {
    return true;
  }

  if (self.rejected) {
    return false;
  }

  let el = self.el;

  if (el.tagName === "IMG") {
    let _load = () => {
      self.loaded = true;
      self.promise.resolve();
    };

    let newSrc = el.getAttribute("data-src");
    let newSrcSet = el.getAttribute("data-srcset");

    if (newSrcSet && el.srcset != newSrcSet) {
      el.srcset = newSrcSet;
    }

    if (newSrc && el.src != newSrc) {
      el.src = newSrc;
    }

    if (el.complete) {
      _load();
    } else {
      el.addEventListener("load", function () {
        _load();
      });
    }
  } else {
    let src = el.getAttribute("data-bg");
    if (!src) {
      // Assume no need to lazy load,
      // perhaps it's just a background image
      // already set.
      self.loaded = true;
      self.promise.resolve();
    } else {
      onSrcLoad(src, function () {
        self.loaded = true;
        Css.backgroundImage(el, src);
        self.promise.resolve();
      });
    }
  }
};

Slide.prototype.reset = function () {
  Velocity(this.el, "stop");
  Css.z(this.el, 0);
  Velocity(
    this.el,
    { opacity: 0, scale: 1, translateX: 0, translateY: 0 },
    { duration: 0 },
  );
};

Slide.prototype.pan = function (isStart) {
  let self = this;

  if (self.slideshow.slides.length === 1) {
    // Just fade in, don't do anything else
    // because we can't loop and this was
    // just a very complex lazyloader.
    self.fadeIn(0);
    return;
  }

  if (!self.loaded) {
    console.warn("Pan was called without loading first");
  }

  // Decide on a direction
  let m = self.slideshow.getDirection();
  let duration = self.slideshow.options.duration;

  let el = self.el;

  // fade ourselves in

  let fadeInDelay = self.slideshow.options.fadeInDelay;

  if (isStart) {
    fadeInDelay = false;
  }

  self.fadeIn(fadeInDelay);
  // start loading the next
  self.next.load();

  // fade the other
  if (Object.prototype.hasOwnProperty.call(self, "prev")) {
    let fadeOutDelay = self.slideshow.options.fadeOutDelay;
    self.prev.fadeOut(fadeOutDelay);
  }

  // animate
  let firedNext = false;

  let rangeX = m.x1 - m.x0;
  let rangeY = m.y1 - m.y0;

  let z = self.slideshow.options.z;

  let animationOptions;

  if (self.slideshow.options.distance > 0) {
    animationOptions = {
      translateX: rangeX,
      translateY: rangeY,
      scale: z,
    };
  } else {
    animationOptions = {
      scale: z,
    };
  }

  self.animation = Velocity(el, animationOptions, {
    queue: false,
    easing: "linear",
    duration: duration,

    // Check time remaining,
    // if we have 33% of the time left
    // then we should trigger the next slide
    // which will fade in and start animating too,
    // thus creating an overlap.

    // Be careful, if the overlap is too close, due to too few elements,
    // you may end up with an animation jump.
    progress: function (elements, complete, remaining) {
      let elapsed = duration - remaining;

      if (elapsed >= self.slideshow.options.playNextAt && !firedNext) {
        firedNext = true;
        if (
          Object.prototype.hasOwnProperty.call(self, "next") &&
          self.slideshow.animating
        ) {
          self.next.panIfLoaded();
        }
      }
    },

    complete: function () {
      Velocity(el, { scale: 1 }, { duration: 1 });
    },
  });
};

/*========================================
=            SlideShow Object            =
========================================*/

const Slideshow = function (options) {
  let self = this;
  self.ready = false;

  // Find the elements:

  // Find the container
  let container;

  if (options.container instanceof HTMLElement) {
    container = options.container;
  } else {
    container = document.querySelector(options.container);
  }

  if (!container) {
    return false;
  }

  // Find the children
  let slideElements = container.querySelectorAll(options.slide);

  // If none, no point doing anything else
  if (slideElements.length === 0) {
    return false;
  }

  // Extend supplied options with defaults
  self.options = extend({}, defaults, options);

  self.animating = false;

  // Init
  self.lastDirection = 0;

  /*==============================
	=            Slides            =
	==============================*/
  self.slides = [];

  elsForEach(slideElements, function (slide, i) {
    let _Slide = new Slide(slide, self);
    self.slides.push(_Slide);
    _Slide.index = i;
    if (i > 0) {
      _Slide.prev = self.slides[i - 1];
    }
  });

  // Once all slides are initialised,
  // go through them all and apply the 'next',
  // as we did not know 'next' when looping
  // through initially:
  self.slides.forEach(function (e, i) {
    if (i + 1 < self.slides.length) {
      e.next = self.slides[i + 1];
    } else {
      // at the end
      e.next = self.slides[0];
    }
  });

  // Finally, our first slide did not know its 'previous'
  // as it will be the last slide, so store that now:
  self.slides[0].prev = self.slides[self.slides.length - 1];

  // Allow apps to say it worked.
  self.ready = true;

  return self;
};

Slideshow.prototype.stop = function () {
  this.animating = false;
  this.slides.forEach(function (s) {
    s.reset();
  });
};

Slideshow.prototype.start = function () {
  this.animating = true;
  this.slides[0].panIfLoaded(true);
};

Slideshow.prototype.getDirection = function () {
  let lastDirection = this.lastDirection;
  let distance = this.options.distance;
  let minPan = this.options.minPan;

  // get a random no from 0 - 3
  let i = rand(0, 3);

  // But avoid using the same one as last time:
  if (i === lastDirection) {
    // Get the next one...
    i = i + 1;

    // if we were on the last one
    // get the first one:
    if (i > 3) {
      i = 0;
    }
  }
  // save this direction as the last:s
  lastDirection = i;

  // Choose direction:
  let direction = directions[i];

  let m = {};
  switch (direction) {
    case "top-left":
      m.x0 = 0 - rand(minPan, distance);
      m.y0 = 0 - rand(minPan, distance);
      m.x1 = rand(minPan, distance);
      m.y1 = rand(minPan, distance);
      break;

    case "top-right":
      m.x0 = rand(minPan, distance);
      m.y0 = 0 - rand(minPan, distance);
      m.x1 = 0 - rand(minPan, distance);
      m.y1 = rand(minPan, distance);
      break;

    case "bottom-left":
      m.x0 = 0 - rand(minPan, distance);
      m.y0 = rand(minPan, distance);
      m.x1 = rand(minPan, distance);
      m.y1 = 0 - rand(minPan, distance);
      break;

    // case 'bottom-right':
    default:
      m.x0 = rand(minPan, distance);
      m.y0 = rand(minPan, distance);
      m.x1 = 0 - rand(minPan, distance);
      m.y1 = 0 - rand(minPan, distance);
      break;
  }

  return m;
};

export default Slideshow;
